import { Dialog } from './Dialog';

/**
 * Result modal for the pixel save flow.
 *
 * When the user clicks Save on any pixel form (admin platform, org default,
 * per-link override), the server verifies the credentials by firing a test
 * PageView event to Meta's Conversions API before persisting. This dialog
 * shows that outcome:
 *
 *   - Verifying: spinner while the request is in flight (the parent owns
 *     the busy state via the disabled save button; we just don't render
 *     when result is null, so the dialog is invisible during the wait).
 *
 *   - Success: green checkmark + a one-line confirmation. Auto-pairs with
 *     a "Saved" indicator next to the button — the dialog explains WHY
 *     the save succeeded (Meta acknowledged).
 *
 *   - Failure: red X + Meta's exact error message + a context-specific
 *     hint based on Meta's error code (invalid token / wrong pixel ID /
 *     missing permission). The form data is preserved in the parent so
 *     the user closes the modal, fixes the cause, and retries.
 */
export interface PixelVerificationResult {
  ok: boolean;
  metaError?: string;
  metaCode?: number;
  httpStatus?: number;
}

export function PixelVerificationDialog({
  open,
  result,
  onClose,
}: {
  open: boolean;
  result: PixelVerificationResult | null;
  onClose: () => void;
}) {
  if (!open || !result) return null;

  const hint = result.ok ? null : hintForError(result);

  return (
    <Dialog open={open} onClose={onClose} labelledBy="pixel-verify-title">
      <div className="w-full max-w-md">
        <div
          className={`flex items-center gap-3 rounded-t-2xl px-5 py-4 ${
            result.ok
              ? 'bg-[color-mix(in_oklab,#16a34a_15%,transparent)]'
              : 'bg-[color-mix(in_oklab,#dc2626_15%,transparent)]'
          }`}
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ${
              result.ok
                ? 'bg-[#16a34a] text-white'
                : 'bg-[#dc2626] text-white'
            }`}
            aria-hidden
          >
            {result.ok ? '✓' : '✗'}
          </div>
          <h2 id="pixel-verify-title" className="text-base font-semibold">
            {result.ok ? 'Pixel verified and saved' : 'Couldn’t verify pixel'}
          </h2>
        </div>

        <div className="space-y-3 px-5 py-4 text-sm">
          {result.ok ? (
            <p className="text-[var(--color-muted-foreground)]">
              Meta accepted a test <code>PageView</code> event from this
              pixel. Your credentials are good — server-side events will
              fire correctly when visitors click your links.
            </p>
          ) : (
            <>
              <p className="text-[var(--color-muted-foreground)]">
                Meta rejected the test event. The pixel was{' '}
                <strong className="text-[var(--color-foreground)]">
                  not saved
                </strong>{' '}
                — fix the issue below and try again.
              </p>
              {result.metaError && (
                <div className="rounded-md border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-3 py-2 text-[13px] text-[var(--color-destructive)]">
                  <div className="font-medium">Meta said:</div>
                  <div className="mt-0.5 font-mono text-[12px] break-words">
                    {result.metaError}
                  </div>
                  {(result.metaCode || result.httpStatus) && (
                    <div className="mt-1 text-[11px] opacity-70">
                      {result.metaCode != null && `code ${result.metaCode}`}
                      {result.metaCode != null && result.httpStatus
                        ? ' · '
                        : ''}
                      {result.httpStatus && `HTTP ${result.httpStatus}`}
                    </div>
                  )}
                </div>
              )}
              {hint && (
                <div className="rounded-md border border-[var(--color-border)] bg-[var(--surface-glass)] px-3 py-2 text-[12px] text-[var(--color-muted-foreground)]">
                  <span className="font-medium text-[var(--color-foreground)]">
                    Likely fix:
                  </span>{' '}
                  {hint}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--color-border)] px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-[var(--color-primary)] px-4 py-1.5 text-sm font-medium text-[var(--color-primary-foreground)]"
            autoFocus
          >
            {result.ok ? 'Done' : 'Close'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Human-readable hint for the most common Meta CAPI error codes seen
 * during pixel-credential validation. Returns null for unknown codes
 * (the raw Meta message above is usually enough).
 */
function hintForError(r: PixelVerificationResult): string | null {
  // OAuth / token issues
  if (r.metaCode === 190) {
    return 'The access token is invalid, expired, or has been revoked. Generate a new one in Meta Events Manager → your pixel → Settings → Conversions API → Generate access token. Then paste the new token here.';
  }
  // Pixel ID resolution
  if (r.metaCode === 803) {
    return 'Meta couldn’t find a pixel with that ID. Double-check the Pixel ID in Meta Events Manager (top-left dropdown shows your pixels) and make sure you’re using the numeric ID, not the name.';
  }
  // Permission issues
  if (r.metaCode === 200 || r.metaCode === 100) {
    return 'The access token doesn’t have permission to send events for this pixel. The token’s user needs at least Advertiser access to the pixel in Business Manager.';
  }
  // Rate limit / throttling
  if (r.metaCode === 4 || r.metaCode === 17 || r.metaCode === 32) {
    return 'You’re hitting Meta’s rate limit. Wait a minute and try again.';
  }
  // Generic 400 with no specific code
  if (r.httpStatus === 400 && !r.metaCode) {
    return 'Meta rejected the request format. Double-check the Pixel ID is correct and the access token was pasted in full.';
  }
  // Network / 5xx
  if (r.httpStatus && r.httpStatus >= 500) {
    return 'Meta is having a server-side issue. Try again in a minute.';
  }
  if (!r.httpStatus) {
    return 'Couldn’t reach Meta’s servers. Check your network and try again.';
  }
  return null;
}
